import { useMutation } from '@apollo/client';
import { useCallback, useRef, useState } from 'react';

import {
  CREATE_PENDING_USER_MUTATION,
  CREATE_SMB_SETUP_INTENT_MUTATION,
  SEND_PASSWORDLESS_OTP_MUTATION,
  UPDATE_PENDING_USER_MUTATION,
  VERIFY_PASSWORDLESS_OTP_MUTATION,
} from '@/auth/store/queries';
import { authSession } from '@/auth/utils/authSession';
import { apiErrorMessage } from '@/core/api';

import type {
  OTPTransport,
  ProfileState,
  Screen,
  SendPasswordlessOTPResponse,
  SignupFormData,
  StripeState,
  UserData,
  VerifyPasswordlessOTPResponse,
} from '../types';
import {
  OTP_ERROR_CODES,
  OTP_ERROR_MESSAGES,
  getErrorCode,
  getErrorMessage,
  hasErrorCode,
  isEmailConflict,
} from '../utils/errorHandling';
import { mapBillingAccount } from '../utils/mapBillingAccount';
import { checkProfileCompleteness } from '../utils/profileCompleteness';
import {
  trackSMBDetailsSubmitted,
  trackSMBError,
  trackSMBOTPSent,
  trackSMBOTPVerified,
  trackSMBPhoneSubmitted,
} from '../utils/smbAnalytics';

interface UseOTPFlowProps {
  onComplete: () => void;
  landingUrl?: string;
}

const SETUP_INTENT_ERROR = 'Unable to set up payment. Please try again.';
const DETAILS_ERROR = 'An unexpected error occurred. Please try again.';

// This flow's own OTP codes have crafted copy. Anything else goes through
// apiErrorMessage, not getErrorMessage, whose raw-text fallback would show
// Apollo's own words for a network failure or a 500 (ENG-5322).
export function otpFlowErrorMessage(err: unknown, fallback: string): string {
  const code = getErrorCode(err);
  if (code && OTP_ERROR_MESSAGES[code]) return OTP_ERROR_MESSAGES[code];
  return apiErrorMessage(err, fallback);
}

export function setupIntentErrorMessage(err: unknown): string {
  return otpFlowErrorMessage(err, SETUP_INTENT_ERROR);
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useOTPFlow({
  onComplete: _onComplete,
  landingUrl: propLandingUrl,
}: UseOTPFlowProps) {
  // Capture landing URL for referral tracking (UTM params, search_id, etc.)
  // Prefer prop from parent (captured at page load with query params intact)
  // Fallback to window.location.href if prop not provided
  const [landingUrl] = useState(() => {
    if (propLandingUrl) {
      return propLandingUrl;
    }
    return typeof window !== 'undefined' ? window.location.href : '';
  });

  // Screen navigation state
  const [screen, setScreen] = useState<Screen>('booking');

  // OTP state
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [transport, setTransport] = useState<OTPTransport>('SMS');
  const [otpVerificationToken, setOtpVerificationToken] = useState<string | null>(null);

  // ============================================================================
  // CONSOLIDATED STATE (Phase 1 Refactor)
  // ============================================================================

  // Consolidated user data - replaces: pendingUserId, existingUserId, pendingUserData,
  // otpUserData, billingAccount, maskedAddress
  const [userData, setUserData] = useState<UserData | null>(null);

  // Consolidated profile state - replaces: partialProfileData, missingFields
  const [profileState, setProfileState] = useState<ProfileState | null>(null);

  // Consolidated Stripe state - replaces: clientSecret, stripeCustomerId
  const [stripeState, setStripeState] = useState<StripeState | null>(null);

  // Form data (will be moved to parent component in next step)
  const [signupFormData, setSignupFormData] = useState<SignupFormData | null>(null);

  // Email conflict state (will be moved to parent component in next step)
  const [conflictEmail, setConflictEmail] = useState<string | null>(null);

  // Loading and error state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tracking refs for analytics
  const verificationStartTime = useRef<Date | null>(null);
  const verificationAttempts = useRef<number>(0);

  // ============================================================================
  // DERIVED VALUES (computed from consolidated state)
  // ============================================================================

  // Whether user is returning (known user, existing or pending-resuming) - determines stepper (2 vs 3 steps)
  // Note: After sendPasswordlessOTP with userFound=true, we set type='existing' but id='' until verification completes
  const isReturningUser = userData?.type === 'existing' || userData?.type === 'pending-resuming';

  // Whether existing user needs billing setup (has account but no billing)
  const isExistingUserWithoutBilling = userData?.type === 'existing' && !userData?.billing;

  // Whether user came via email/password login (existing user without OTP token)
  const isExistingUserFromEmailLogin = userData?.type === 'existing' && !otpVerificationToken;

  // Masked address for display (from userData or empty)
  const maskedAddress = userData?.maskedAddress ?? '';

  // Billing account from userData (for backward compatibility)
  const billingAccount = userData?.billing ?? null;

  // Individual user IDs for backward compatibility during transition
  const pendingUserId =
    userData?.type === 'new' || userData?.type === 'pending-resuming' ? userData.id : null;
  const existingUserId = userData?.type === 'existing' ? userData.id : null;

  // GraphQL mutations
  const [sendOTP] = useMutation<SendPasswordlessOTPResponse>(SEND_PASSWORDLESS_OTP_MUTATION);
  const [verifyOTP] = useMutation<VerifyPasswordlessOTPResponse>(VERIFY_PASSWORDLESS_OTP_MUTATION);
  const [createSetupIntent] = useMutation(CREATE_SMB_SETUP_INTENT_MUTATION);
  const [createPendingUser] = useMutation(CREATE_PENDING_USER_MUTATION);
  const [updatePendingUser] = useMutation(UPDATE_PENDING_USER_MUTATION);

  /**
   * Helper to create SetupIntent and store the result.
   * Centralizes logic for Stripe SetupIntent creation.
   * Backend auto-detects auth method (OTP token vs Bearer token).
   *
   * @param otpToken - The OTP verification token (optional - if absent, uses Bearer auth)
   * @returns true if successful, false if failed (error is set internally)
   */
  const createAndStoreSetupIntent = useCallback(
    async (otpToken: string | null | undefined): Promise<boolean> => {
      try {
        let data;

        // If user is authenticated (has Bearer token), use authenticated client
        if (authSession.isAuthenticated()) {
          const authClient = authSession.createClient();
          const response = await authClient.client.mutate({
            mutation: CREATE_SMB_SETUP_INTENT_MUTATION,
            variables: {
              input: {}, // Omit OTP token, uses Bearer token from Authorization header
            },
          });
          data = response.data;
        } else {
          // Use OTP token flow for unauthenticated users
          const response = await createSetupIntent({
            variables: {
              input: { otpVerificationToken: otpToken || undefined },
            },
          });
          data = response.data;
        }

        const result = data?.createSMBSetupIntent;
        if (!result) {
          throw new Error('Failed to create payment setup');
        }

        setStripeState({
          clientSecret: result.clientSecret,
          customerId: result.stripeCustomerId,
        });
        return true;
      } catch (err) {
        console.error('Failed to create SetupIntent:', err);
        setError(setupIntentErrorMessage(err));
        // The fixed line and the code, not the shown text: API copy would make the
        // label high-cardinality and can echo what the user typed.
        trackSMBError('api_error', 'payment', SETUP_INTENT_ERROR, getErrorCode(err) ?? undefined);
        return false;
      }
    },
    [createSetupIntent]
  );

  // Clear error when changing screens
  const goToScreen = useCallback((newScreen: Screen) => {
    setError(null);
    setScreen(newScreen);
  }, []);

  // Handle sending OTP code
  const handleSendCode = useCallback(
    async (address: string, transportType: OTPTransport) => {
      setIsLoading(true);
      setError(null);

      try {
        const { data } = await sendOTP({
          variables: {
            address,
            transport: transportType,
          },
        });

        const result = data?.sendPasswordlessOTP;
        if (!result?.success) {
          throw new Error('Failed to send verification code');
        }

        // Track phone submission and OTP sent (after successful send)
        const countryCodeMatch = address.match(/^\+(\d{1,3})/);
        const countryCode = countryCodeMatch ? `+${countryCodeMatch[1]}` : '+1';
        const isReturning = !!result.userFound;
        const phonePrefilled = false;
        trackSMBPhoneSubmitted(countryCode, isReturning, phonePrefilled);

        const isResend = verificationStartTime.current !== null;
        trackSMBOTPSent(transportType === 'SMS' ? 'sms' : 'email', result.maskedAddress, isResend);

        // Store OTP data
        setVerifiedPhone(address);
        setTransport(transportType);

        // Initialize verification tracking
        verificationStartTime.current = new Date();
        verificationAttempts.current = 0;

        // Store user data if returning user (known phone)
        // Note: At this point we don't have a user ID yet, but we know it's a known user
        // The full userData will be populated after OTP verification
        if (result.userFound) {
          // Temporarily store minimal user data for display
          // Full userData will be set after verification
          setUserData({
            id: '', // Will be set after verification
            type: 'existing', // Known user
            name: result.userName || '',
            company: result.userCompany || '',
            maskedAddress: result.maskedAddress,
            billing: null, // Will be set after verification
          });
        } else {
          setUserData(null);
        }

        // Navigate to verification screen
        goToScreen('verify');
      } catch (err) {
        // API returns OTP_EXPIRED (error code 60203) for both "verification expired"
        // and "max send attempts reached". During send operation, treat it as rate limit.
        let errorType: 'otp_expired' | 'network_error' | 'api_error' = 'api_error';
        let errorMessage = getErrorMessage(err);

        if (hasErrorCode(err, OTP_ERROR_CODES.OTP_EXPIRED)) {
          errorType = 'otp_expired';
          errorMessage =
            'Too many verification attempts. Please wait 10 minutes before trying again.';
          setError(errorMessage);
        } else {
          errorMessage = getErrorMessage(err);
          setError(errorMessage);
        }

        // Track error
        const errorCode = hasErrorCode(err, OTP_ERROR_CODES.OTP_EXPIRED)
          ? OTP_ERROR_CODES.OTP_EXPIRED
          : undefined;
        trackSMBError(errorType, 'phone', errorMessage, errorCode);
      } finally {
        setIsLoading(false);
      }
    },
    [sendOTP, goToScreen]
  );

  // Handle OTP verification
  const handleVerifyCode = useCallback(
    async (code: string) => {
      setIsLoading(true);
      setError(null);

      try {
        // Increment verification attempts
        verificationAttempts.current += 1;

        const { data } = await verifyOTP({
          variables: {
            address: verifiedPhone,
            code,
            transport,
          },
        });

        const result = data?.verifyPasswordlessOTP;
        if (!result?.success) {
          throw new Error('Invalid verification code');
        }

        // Calculate verification duration
        const verificationDuration = verificationStartTime.current
          ? Math.round((new Date().getTime() - verificationStartTime.current.getTime()) / 1000)
          : 0;

        // Determine user type for tracking
        const isReturning = !!(result.token || result.pendingUser);
        const userId = result.user?.id || result.pendingUser?.userId || '';

        // Track OTP verification success
        trackSMBOTPVerified(
          isReturning,
          userId,
          verificationDuration,
          verificationAttempts.current
        );

        // Store OTP verification token
        if (result.otpVerificationToken) {
          setOtpVerificationToken(result.otpVerificationToken);
        }

        // Handle 3 user states:
        // 1. Active user - has token (fetch user data via authenticated query)
        if (result.token) {
          // Authenticate: store token, fetch viewer, sync Redux
          const viewer = await authSession.authenticate(result.token);

          // Use viewer directly - it has all the fields we need (profile, groups, email)
          const user = viewer;

          // Get user's group for profile completeness and billing check
          const userGroup = user.groups?.[0];
          const groupBillingAccount = userGroup?.billing_account;

          // Check profile completeness before routing
          const profileCheck = checkProfileCompleteness(user, userGroup);

          // Map GraphQL response to BillingAccountInfo interface
          const mappedBillingAccount = mapBillingAccount(
            groupBillingAccount as Parameters<typeof mapBillingAccount>[0]
          );

          // Set consolidated user data for existing user
          setUserData({
            id: user.id,
            type: 'existing',
            name: user.profile?.name || `${user.profile?.first_name} ${user.profile?.last_name}`,
            company: userGroup?.name || userData?.company || '',
            maskedAddress: userData?.maskedAddress || '',
            billing: mappedBillingAccount,
            groupId: userGroup?.id,
          });

          // Route based on profile completeness and billing account
          // Priority: 1) Incomplete profile → details, 2) No billing → payment setup, 3) Has billing → confirmation
          if (!profileCheck.isComplete) {
            // Incomplete profile - route to details with only missing fields
            setProfileState({
              isPartial: true,
              missingFields: profileCheck.missingFields,
              existingData: profileCheck.existingData,
            });
            goToScreen('details');
            return;
          }

          if (mappedBillingAccount) {
            // Complete profile + has billing account - show existing payment info
            goToScreen('payment-existing');
          } else {
            // No billing account - need to create one via Stripe
            // Use createSMBSetupIntent for existing users verified via OTP
            // (isExistingUserWithoutBilling is now derived from userData)

            // Create SetupIntent for payment
            const success = await createAndStoreSetupIntent(result.otpVerificationToken);
            if (!success) return;

            goToScreen('payment-new');
          }
          return;
        }

        // 2. Pending user - resume signup from payment (Flow 3b)
        if (result.pendingUser) {
          // Set consolidated user data for pending user resuming
          setUserData({
            id: result.pendingUser.userId,
            type: 'pending-resuming',
            name: `${result.pendingUser.firstName} ${result.pendingUser.lastName}`,
            company: result.pendingUser.companyName,
            maskedAddress: userData?.maskedAddress || '',
            billing: null,
            pendingDetails: {
              firstName: result.pendingUser.firstName,
              lastName: result.pendingUser.lastName,
              email: result.pendingUser.email,
              companyName: result.pendingUser.companyName,
              hasStripeCustomer: result.pendingUser.hasStripeCustomer,
            },
          });

          // Always create a new SetupIntent for pending users resuming signup.
          // Even if hasStripeCustomer is true (from a previous abandoned session),
          // we need a fresh clientSecret for this payment session.
          // The API handles reusing existing Stripe customers - won't create duplicates.
          const success = await createAndStoreSetupIntent(result.otpVerificationToken);
          if (!success) return;

          goToScreen('payment-new');
          return;
        }

        // 3. New user - go to details form
        goToScreen('details');
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);

        // Determine error type for tracking
        let errorType: 'otp_invalid' | 'otp_expired' | 'api_error' = 'api_error';
        if (hasErrorCode(err, OTP_ERROR_CODES.OTP_INVALID)) {
          errorType = 'otp_invalid';
        } else if (hasErrorCode(err, OTP_ERROR_CODES.OTP_EXPIRED)) {
          errorType = 'otp_expired';
        }

        // Extract error code if available
        const errorCode = hasErrorCode(err, OTP_ERROR_CODES.OTP_INVALID)
          ? OTP_ERROR_CODES.OTP_INVALID
          : hasErrorCode(err, OTP_ERROR_CODES.OTP_EXPIRED)
            ? OTP_ERROR_CODES.OTP_EXPIRED
            : undefined;

        // Track OTP verification error
        trackSMBError(errorType, 'otp', errorMessage, errorCode);
      } finally {
        setIsLoading(false);
      }
    },
    [verifyOTP, verifiedPhone, transport, userData, createAndStoreSetupIntent, goToScreen]
  );

  // Handle resend OTP
  const handleResendCode = useCallback(async () => {
    if (!verifiedPhone) return;
    await handleSendCode(verifiedPhone, transport);
  }, [verifiedPhone, transport, handleSendCode]);

  // Handle "use another account" - reset and go back to phone
  const handleUseAnotherAccount = useCallback(() => {
    setUserData(null);
    setProfileState(null);
    setStripeState(null);
    setVerifiedPhone('');
    setOtpVerificationToken(null);
    goToScreen('phone');
  }, [goToScreen]);

  // Handle details form submission
  // For new users: createPendingUser → createSMBSetupIntent
  // For existing users with incomplete profile: updatePendingUser → check billing
  const handleDetailsSubmit = useCallback(
    async (formData: {
      firstName: string;
      lastName: string;
      email: string;
      company: string;
      jobTitle?: string;
      password: string;
    }) => {
      setIsLoading(true);
      setError(null);
      setSignupFormData(formData);

      try {
        // OTP token is required for all mutations
        if (!otpVerificationToken) {
          throw new Error('Missing OTP verification token');
        }

        // Branch based on user type (existing user vs new user)
        if (userData?.type === 'existing' && userData.id) {
          // EXISTING USER with incomplete profile - update their data
          await updatePendingUser({
            variables: {
              input: {
                otpVerificationToken,
                userId: userData.id,
                firstName: formData.firstName || undefined,
                lastName: formData.lastName || undefined,
                companyName: formData.company || undefined,
              },
            },
          });

          // Track details form submission (after successful mutation)
          const isExistingUser = true;
          const hasCompany = !!(formData.company && formData.company.trim());
          const isPartialForm = profileState?.isPartial || false;
          const emailDomain = formData.email ? formData.email.split('@')[1] || '' : '';
          trackSMBDetailsSubmitted(isExistingUser, hasCompany, emailDomain, isPartialForm);

          // Existing user already has billing - go to payment confirmation
          if (userData.billing) {
            goToScreen('payment-existing');
            return;
          }

          // Existing user needs billing setup - create SetupIntent
          // (isExistingUserWithoutBilling is now derived from userData)
          const success = await createAndStoreSetupIntent(otpVerificationToken);
          if (!success) return;
          goToScreen('payment-new');
        } else {
          // NEW USER - create pending user first
          const { data: pendingData } = await createPendingUser({
            variables: {
              input: {
                otpVerificationToken,
                email: formData.email,
                password: formData.password,
                firstName: formData.firstName,
                lastName: formData.lastName,
                companyName: formData.company,
                jobTitle: formData.jobTitle || undefined,
                landingUrl: landingUrl || undefined,
              },
            },
          });

          const pendingUser = pendingData?.createPendingUser;
          if (!pendingUser?.userId) {
            throw new Error('Failed to create user');
          }

          // Set user data for newly created pending user
          setUserData({
            id: pendingUser.userId,
            type: 'new',
            name: `${formData.firstName} ${formData.lastName}`,
            company: formData.company,
            maskedAddress: userData?.maskedAddress || '',
            billing: null,
          });

          // Track details form submission (after successful user creation)
          const hasCompanyNew = !!(formData.company && formData.company.trim());
          const isPartialFormNew = profileState?.isPartial || false;
          const emailDomainNew = formData.email ? formData.email.split('@')[1] || '' : '';
          trackSMBDetailsSubmitted(false, hasCompanyNew, emailDomainNew, isPartialFormNew);

          // Create SetupIntent using OTP token
          const success = await createAndStoreSetupIntent(otpVerificationToken);
          if (!success) return;
          goToScreen('payment-new');
        }
      } catch (err) {
        // Check for email conflict
        if (isEmailConflict(err)) {
          setConflictEmail(formData.email);
          goToScreen('email-conflict');
          // Don't track email conflict as error - it's a valid flow handled by UI
        } else {
          const errorMessage = otpFlowErrorMessage(err, DETAILS_ERROR);
          setError(errorMessage);

          // Track details submission error
          const errorCode = hasErrorCode(err, OTP_ERROR_CODES.OTP_TOKEN_EXPIRED)
            ? OTP_ERROR_CODES.OTP_TOKEN_EXPIRED
            : undefined;
          trackSMBError('validation_error', 'details', errorMessage, errorCode);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      userData,
      profileState,
      createPendingUser,
      updatePendingUser,
      createAndStoreSetupIntent,
      otpVerificationToken,
      goToScreen,
      landingUrl,
    ]
  );

  // Handle email conflict resolution success
  // Now fetches user data via authenticated query since mutation only returns token
  const handleLinkSuccess = useCallback(
    async (result: { success: boolean; token: string }) => {
      setIsLoading(true);
      try {
        // Authenticate: store token, fetch viewer, sync Redux
        const viewer = await authSession.authenticate(result.token);

        // Use viewer directly - it has all the fields we need (profile, groups, email)
        const user = viewer;

        // Get user's group for billing check
        const userGroup = user.groups?.[0];
        const groupBillingAccount = userGroup?.billing_account;

        // Map billing account
        const mappedBillingAccount = mapBillingAccount(
          groupBillingAccount as Parameters<typeof mapBillingAccount>[0]
        );

        // Update consolidated user data
        setUserData({
          id: user.id,
          type: 'existing',
          name: user.profile?.name || `${user.profile?.first_name} ${user.profile?.last_name}`,
          company: userGroup?.name || userData?.company || '',
          maskedAddress: userData?.maskedAddress || '',
          billing: mappedBillingAccount,
          groupId: userGroup?.id,
        });

        // Navigate to account linked screen
        goToScreen('account-linked');
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    },
    [userData, goToScreen]
  );

  // Handle "use different email" from email conflict
  const handleUseDifferentEmail = useCallback(() => {
    setConflictEmail(null);
    goToScreen('details');
  }, [goToScreen]);

  // Handle payment success
  const handlePaymentSuccess = useCallback(() => {
    goToScreen('confirmation');
  }, [goToScreen]);

  // Handle payment error
  const handlePaymentError = useCallback((message: string) => {
    setError(message);
  }, []);

  // Get current step index for stepper (based on screen)
  const getStepperIndex = useCallback(() => {
    switch (screen) {
      case 'phone':
      case 'verify':
        return 0;
      case 'details':
        return 1;
      case 'payment-new':
      case 'payment-existing':
        return isReturningUser ? 1 : 2;
      default:
        return 0;
    }
  }, [screen, isReturningUser]);

  // Determine which stepper to show
  const getStepperSteps = useCallback(() => {
    if (isReturningUser || pendingUserId) {
      return ['Verify Phone', 'Payment'];
    }
    return ['Verify Phone', 'Your Details', 'Payment'];
  }, [isReturningUser, pendingUserId]);

  // Helper to get clientSecret from stripeState (for backward compatibility)
  const clientSecret = stripeState?.clientSecret ?? null;
  const stripeCustomerId = stripeState?.customerId ?? null;

  // Helper to get profile data (for backward compatibility)
  const partialProfileData = profileState?.existingData ?? null;
  const missingFields = profileState?.missingFields ?? [];

  return {
    // Screen state
    screen,
    goToScreen,

    // OTP state
    verifiedPhone,
    transport,
    maskedAddress, // Derived from userData
    otpVerificationToken,

    // ============================================================================
    // CONSOLIDATED STATE (new API)
    // ============================================================================
    userData,
    setUserData,
    profileState,
    setProfileState,
    stripeState,
    setStripeState,

    // ============================================================================
    // BACKWARD COMPATIBLE API (derived values)
    // These are computed from consolidated state for gradual migration
    // ============================================================================

    // User state (derived from userData)
    isReturningUser, // Derived: !!userData?.id
    pendingUserId, // Derived: userData?.type === 'new' || 'pending-resuming' ? userData.id : null
    existingUserId, // Derived: userData?.type === 'existing' ? userData.id : null
    billingAccount, // Derived: userData?.billing
    isExistingUserWithoutBilling, // Derived: userData?.type === 'existing' && !userData?.billing
    isExistingUserFromEmailLogin, // Derived: userData?.type === 'existing' && !otpVerificationToken

    // Profile state (derived from profileState)
    partialProfileData, // Derived: profileState?.existingData
    missingFields, // Derived: profileState?.missingFields ?? []

    // Email conflict state
    conflictEmail,
    setConflictEmail,

    // Stripe state (derived from stripeState)
    clientSecret, // Derived: stripeState?.clientSecret
    stripeCustomerId, // Derived: stripeState?.customerId

    // Form data
    signupFormData,
    setSignupFormData,

    // Loading/error state
    isLoading,
    error,

    // Handlers
    handleSendCode,
    handleVerifyCode,
    handleResendCode,
    handleUseAnotherAccount,
    handleDetailsSubmit,
    handleLinkSuccess,
    handleUseDifferentEmail,
    handlePaymentSuccess,
    handlePaymentError,

    // Stepper helpers
    getStepperIndex,
    getStepperSteps,
  };
}
